import { useState, useEffect, useRef } from 'react';
import { Play, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OurWork() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null);
  const marqueeRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/gallery`).then(r => setItems(r.data.items)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const photos = items.filter(i => i.media_type === 'photo');
  const videos = items.filter(i => i.media_type === 'video');
  const marquees = items.filter(i => i.media_type === 'marquee');

  const filtered = filter === 'all' ? items.filter(i => i.media_type !== 'marquee') :
    filter === 'photo' ? photos :
    filter === 'video' ? videos : [];

  return (
    <div className="min-h-screen bg-cream" data-testid="our-work-page">
      {/* Hero Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1C1209 0%, #3A2510 100%)',
        padding: '60px 0 50px',
        position: 'relative', overflow: 'hidden', textAlign: 'center',
      }}>
        <div style={{
          position: 'absolute', top: '-80px', right: '-80px',
          width: '300px', height: '300px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(232,140,63,0.2) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '-60px', left: '-60px',
          width: '200px', height: '200px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(196,94,42,0.15) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '700px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: 'rgba(196,94,42,0.2)', border: '1px solid rgba(196,94,42,0.3)',
            padding: '6px 18px', borderRadius: '100px',
            fontSize: '0.75rem', fontWeight: 600, color: '#E88C3F',
            letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '20px',
          }}>
            ✨ Our Portfolio
          </div>
          <h1 className="font-heading" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 900, color: '#FFF8F0', marginBottom: '12px', lineHeight: 1.1 }}>
            Our <span style={{ color: '#E88C3F', fontStyle: 'italic' }}>Work</span>
          </h1>
          <p className="font-body" style={{ fontSize: '1rem', color: 'rgba(255,248,240,0.6)', fontWeight: 300, lineHeight: 1.7 }}>
            Browse through our gallery of happy pets, grooming sessions, and everything we do at Pets Valley
          </p>
        </div>
      </div>

      {/* Marquee Section */}
      {marquees.length > 0 && (() => {
        // Guarantee at least 80 items total to comfortably fill dual 4K monitors
        const minItems = 80;
        let reps = Math.max(12, Math.ceil(minItems / marquees.length));
        if (reps % 2 !== 0) reps++; // Must be even for seamless 50% translation
        const duration = Math.max(marquees.length * reps * 2, 20); // Constant speed mapping

        return (
          <div style={{ overflow: 'hidden', background: '#1C1209', padding: '16px 0', width: '100vw', marginLeft: 'calc(-50vw + 50%)' }}>
            <div style={{
              display: 'flex', gap: '16px', paddingRight: '16px',
              animation: `marqueeScroll ${duration}s linear infinite`,
              width: 'max-content',
            }}>
              {Array.from({ length: reps }).flatMap(() => marquees).map((m, i) => (
                <img key={`${m.id}-${i}`} src={m.media_url} alt={m.title || 'Our work'}
                  style={{
                    height: '80px', width: 'auto', borderRadius: '12px',
                    objectFit: 'cover', cursor: 'pointer', opacity: 0.85,
                    transition: 'opacity 0.3s, transform 0.3s', flexShrink: 0
                  }}
                  onClick={() => setLightbox(m)}
                  onMouseEnter={e => { e.target.style.opacity = '1'; e.target.style.transform = 'scale(1.05)'; e.target.parentElement.style.animationPlayState = 'paused'; }}
                  onMouseLeave={e => { e.target.style.opacity = '0.85'; e.target.style.transform = 'scale(1)'; e.target.parentElement.style.animationPlayState = 'running'; }}
                  onError={e => { e.target.style.display = 'none'; }}
                />
              ))}
            </div>
          </div>
        );
      })()}

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 24px 80px' }}>
        {/* Filter Tabs */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '32px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {[
            { value: 'all', label: '🎨 All Work' },
            { value: 'photo', label: '📸 Photos' },
            { value: 'video', label: '🎬 Videos' },
          ].map(tab => (
            <button key={tab.value} onClick={() => setFilter(tab.value)} className="font-body"
              style={{
                padding: '10px 24px', borderRadius: '100px', fontSize: '0.88rem', fontWeight: 600,
                cursor: 'pointer', border: 'none', transition: 'all 0.3s',
                background: filter === tab.value ? '#C45E2A' : '#fff',
                color: filter === tab.value ? '#fff' : '#4A3728',
                boxShadow: filter === tab.value ? '0 4px 16px rgba(196,94,42,0.3)' : '0 2px 8px rgba(0,0,0,0.06)',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}><div className="spinner"></div></div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🐾</div>
            <p className="font-body" style={{ fontSize: '1.1rem', color: '#8B6A52', marginBottom: '8px' }}>No items to show yet</p>
            <p className="font-body" style={{ fontSize: '0.85rem', color: '#C4B5A5' }}>Check back soon for updates!</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px',
          }}
            className="ourwork-grid"
          >
            {filtered.map((item, idx) => (
              <div key={item.id}
                onClick={() => setLightbox(item)}
                className="group"
                style={{
                  borderRadius: '20px', overflow: 'hidden', cursor: 'pointer',
                  background: '#fff', transition: 'transform 0.3s, box-shadow 0.3s',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  animationDelay: `${idx * 0.05}s`,
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
              >
                <div style={{ position: 'relative', aspectRatio: item.media_type === 'video' ? '16/9' : '4/3', overflow: 'hidden' }}>
                  {item.media_type === 'video' ? (
                    <>
                      <video src={item.media_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted preload="metadata" />
                      <div style={{
                        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(0,0,0,0.3)',
                      }}>
                        <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
                          <Play style={{ width: '24px', height: '24px', color: '#C45E2A', marginLeft: '3px' }} />
                        </div>
                      </div>
                    </>
                  ) : (
                    <img src={item.media_url} alt={item.title || 'Our work'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s' }}
                      onError={e => { e.target.src = 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=500&h=400&fit=crop'; }}
                      onMouseEnter={e => { e.target.style.transform = 'scale(1.08)'; }}
                      onMouseLeave={e => { e.target.style.transform = 'scale(1)'; }}
                    />
                  )}
                </div>
                {(item.title || item.description) && (
                  <div style={{ padding: '16px 18px' }}>
                    {item.title && <h3 className="font-body" style={{ fontWeight: 600, color: '#1C1209', fontSize: '0.95rem', marginBottom: '4px' }}>{item.title}</h3>}
                    {item.description && <p className="font-body" style={{ fontSize: '0.82rem', color: '#8B6A52', lineHeight: 1.6, fontWeight: 300 }}>{item.description}</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 999,
          background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(12px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px', animation: 'lpFadeIn 0.3s ease',
        }}
          onClick={() => setLightbox(null)}
        >
          <button onClick={() => setLightbox(null)} style={{
            position: 'absolute', top: '20px', right: '20px',
            width: '44px', height: '44px', borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)', border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', transition: 'background 0.2s',
          }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
          >
            <X style={{ width: '20px', height: '20px', color: '#fff' }} />
          </button>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '85vh', position: 'relative' }}>
            {lightbox.media_type === 'video' ? (
              <video src={lightbox.media_url} controls autoPlay style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: '16px' }} />
            ) : (
              <img src={lightbox.media_url} alt={lightbox.title || ''}
                style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: '16px', objectFit: 'contain' }} />
            )}
            {lightbox.title && (
              <div style={{ position: 'absolute', bottom: '0', left: '0', right: '0', padding: '20px 24px', background: 'linear-gradient(transparent, rgba(0,0,0,0.8))', borderRadius: '0 0 16px 16px' }}>
                <p className="font-body" style={{ color: '#fff', fontWeight: 600, fontSize: '1rem' }}>{lightbox.title}</p>
                {lightbox.description && <p className="font-body" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginTop: '4px' }}>{lightbox.description}</p>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Marquee animation CSS */}
      <style>{`
        @keyframes marqueeScroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        @media (min-width: 768px) {
          .ourwork-grid { grid-template-columns: repeat(3, 1fr) !important; gap: 20px !important; }
        }
        @media (min-width: 1280px) {
          .ourwork-grid { grid-template-columns: repeat(4, 1fr) !important; }
        }
      `}</style>
    </div>
  );
}
